import React, { useState } from "react";
import { Globe, Check, X } from "lucide-react";
import { useAppContext } from "../../AppContext";
import { useTranslation } from "react-i18next";

export default function AccountAntSetting() {
  const { t } = useTranslation();

  const { Lang, setLang } = useAppContext();
  const [languageModalVisible, setLanguageModalVisible] = useState(false);
  const isRTL = Lang === "ar" || Lang === "he";

  const languages = [
    {
      code: "en",
      name: t("languageEnglish"),
      nativeName: "English",
      flag: "🇺🇸",
      direction: "ltr",
    },
    {
      code: "ar",
      name: t("languageArabic"),
      nativeName: "العربية",
      flag: "🇸🇦",
      direction: "rtl",
    },
    {
      code: "he",
      name: t("languageHebrew"),
      nativeName: "עברית",
      flag: "🇮🇱",
      direction: "rtl",
    },
  ];

  const handleLanguageChange = (langCode) => {
    setLang(langCode);
    const selectedLang = languages.find((lang) => lang.code === langCode);
    if (selectedLang) {
      document.documentElement.dir = selectedLang.direction;
    }
    setLanguageModalVisible(false);
  };

  const getCurrentLanguage = () => {
    return languages.find((lang) => lang.code === Lang) || languages[0];
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          padding: "20px",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {/* Header */}
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            marginBottom: "32px",
          }}
        >
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "8px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("settings")}
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#6B7280",
              margin: 0,
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("manageYourPreferences")}
          </p>
        </div>

        {/* Settings Card */}
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            backgroundColor: "#FFFFFF",
            borderRadius: "16px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
        >
          {/* Language Setting */}
          <div
            style={{
              padding: "20px",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    backgroundColor: "#3B82F615",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Globe size={24} color="#3B82F6" />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#111827",
                      margin: "0 0 4px 0",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {t("language")}
                  </h3>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#6B7280",
                      margin: 0,
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {getCurrentLanguage().nativeName}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setLanguageModalVisible(true)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "10px",
                  backgroundColor: "#3B82F6",
                  border: "none",
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2563EB";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(59, 130, 246, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#3B82F6";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {t("change")}
              </button>
            </div>
          </div>

          {/* More settings can be added here */}
        </div>
      </div>

      {/* Language Selection Modal */}
      {languageModalVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            animation: "fadeIn 0.2s ease-out",
          }}
          onClick={() => setLanguageModalVisible(false)}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "20px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              animation: "slideUp 0.3s ease-out",
              direction: isRTL ? "rtl" : "ltr",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "14px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#3B82F615",
                  }}
                >
                  <Globe size={32} color="#3B82F6" />
                </div>
                <div style={{ flex: 1 }}>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#1F2937",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {t("selectLanguage")}
                  </h2>
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "14px",
                      color: "#6B7280",
                      textAlign: isRTL ? "right" : "left",
                    }}
                  >
                    {t("chooseYourPreferredLanguage")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setLanguageModalVisible(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3F4F6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <X size={24} color="#6B7280" />
              </button>
            </div>

            {/* Language Options */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {languages.map((language) => {
                const isSelected = Lang === language.code;

                return (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    style={{
                      padding: "20px",
                      borderRadius: "14px",
                      border: `2px solid ${isSelected ? "#3B82F6" : "#E5E7EB"}`,
                      backgroundColor: isSelected ? "#EFF6FF" : "#FFFFFF",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flexDirection: isRTL ? "row-reverse" : "row",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#3B82F6";
                        e.currentTarget.style.backgroundColor = "#F9FAFB";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0, 0, 0, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#E5E7EB";
                        e.currentTarget.style.backgroundColor = "#FFFFFF";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }
                    }}
                  >
                    {/* Flag */}
                    <div
                      style={{
                        fontSize: "32px",
                        flexShrink: 0,
                      }}
                    >
                      {language.flag}
                    </div>

                    {/* Language Info */}
                    <div
                      style={{
                        flex: 1,
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#1F2937",
                        }}
                      >
                        {language.name}
                      </h3>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "14px",
                          color: "#6B7280",
                        }}
                      >
                        {language.nativeName}
                      </p>
                    </div>

                    {/* Check Icon */}
                    {isSelected && (
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "16px",
                          backgroundColor: "#3B82F6",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Check size={20} color="#FFFFFF" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
